import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extension-placeholder';
import Toolbar from './toolbar';
import './text-editor.scss';

export interface TextEditorProps {
	value?: string;
	onChange?: (value: string) => void;
	placeholder?: string;
	readOnly?: boolean;
	className?: string;
	style?: React.CSSProperties;
	height?: string | number;
	minHeight?: string | number;
	maxHeight?: string | number;
}

const TextEditor: React.FC<TextEditorProps> = ({
	value = '',
	onChange,
	placeholder = 'Start writing...',
	readOnly = false,
	className = '',
	style,
	height = '300px',
	minHeight = '200px',
	maxHeight = '600px',
}) => {
	const [editorValue, setEditorValue] = useState(value);

	// Tiptap extensions configuration
	const extensions = [
		StarterKit,
		TextAlign,
		Color,
		TextStyle,
		Placeholder.configure({
			placeholder,
		}),
	];

	const editor = useEditor({
		extensions,
		content: editorValue,
		editable: !readOnly,
		onUpdate: ({ editor }) => {
			const html = editor.getHTML();
			setEditorValue(html);
			if (onChange) {
				onChange(html);
			}
		},
	});

	useEffect(() => {
		if (value !== editorValue && editor) {
			editor.commands.setContent(value);
			setEditorValue(value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, editor]);

	return (
		<div
			className={`text-editor-container ${className}`}
			style={{
				height,
				minHeight,
				maxHeight,
				...style,
			}}
		>
			<Toolbar editor={editor} />
			<EditorContent
				editor={editor}
				style={{
					height: 'calc(100% - 40px)',
					overflow: 'auto',
				}}
			/>
		</div>
	);
};

export default TextEditor;
